class CartGiftProgress extends HTMLElement {
  constructor() {
    super();
    this.handleCartUpdated = this.handleCartUpdated.bind(this);
  }

  connectedCallback() {
    this.endpointUrl = this.dataset.endpointUrl || "";
    this.handles = (this.dataset.handles || "")
      .split(",")
      .map((h) => h.trim())
      .filter(Boolean);
    this.handlesKey = [...this.handles].sort().join("|");
    this.minimumCents = parseInt(this.dataset.minimumCents, 10) || 0;
    this.bannerMessage = this.dataset.bannerMessage || "";
    this.giftAddedText = this.dataset.giftAddedText || "";

    this.eligibilityCache = new Map();
    this.inFlight = new Map();

    try {
      const initial = JSON.parse(this.dataset.eligibility || "{}");
      for (const [productId, eligible] of Object.entries(initial)) {
        this.eligibilityCache.set(this.cacheKey(productId), Boolean(eligible));
      }
    } catch (_error) {
      // Ignore malformed JSON; cache simply starts empty.
    }

    document.addEventListener("cart:updated", this.handleCartUpdated);
  }

  disconnectedCallback() {
    document.removeEventListener("cart:updated", this.handleCartUpdated);
  }

  cacheKey(productId) {
    return `${productId}|${this.handlesKey}`;
  }

  async handleCartUpdated(event) {
    const cart = event?.detail?.cart;
    if (!cart || !Array.isArray(cart.items)) return;

    const lookups = [];
    for (const item of cart.items) {
      const productId = item.product_id;
      if (productId == null) continue;
      if (this.eligibilityCache.has(this.cacheKey(productId))) continue;
      lookups.push(this.fetchEligibility(productId));
    }
    if (lookups.length > 0) {
      await Promise.all(lookups);
    }

    let eligibleCents = 0;
    for (const item of cart.items) {
      const productId = item.product_id;
      if (productId == null) continue;
      if (this.eligibilityCache.get(this.cacheKey(productId)) === true) {
        eligibleCents += Number(item.final_line_price) || 0;
      }
    }

    this.render(eligibleCents, cart);
  }

  async fetchEligibility(productId) {
    if (!this.endpointUrl || this.handles.length === 0) {
      this.eligibilityCache.set(this.cacheKey(productId), false);
      return;
    }
    const key = this.cacheKey(productId);
    if (this.inFlight.has(key)) return this.inFlight.get(key);

    const promise = (async () => {
      try {
        const response = await fetch(this.endpointUrl, {
          method: "POST",
          headers: {"content-type": "application/json"},
          body: JSON.stringify({
            productId: `gid://shopify/Product/${productId}`,
            handles: this.handles,
          }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        this.eligibilityCache.set(key, Boolean(payload?.eligible));
      } catch (error) {
        console.error("cart-gift-progress: eligibility lookup failed", error);
        this.eligibilityCache.set(key, false);
      } finally {
        this.inFlight.delete(key);
      }
    })();

    this.inFlight.set(key, promise);
    return promise;
  }

  render(eligibleCents, cart) {
    const banner = this.querySelector("[data-banner]");
    if (!banner) return;

    const thresholdMet =
      this.minimumCents > 0 && eligibleCents >= this.minimumCents;

    this.classList.toggle("cart-gift-progress--success", thresholdMet);

    const ratio =
      this.minimumCents > 0
        ? Math.min(1, Math.max(0, eligibleCents / this.minimumCents))
        : 0;
    const percent = Math.round(ratio * 100);
    const cappedEligibleCents = Math.min(eligibleCents, this.minimumCents);
    const currentFormatted = formatMoney(cappedEligibleCents, cart);
    const targetFormatted = formatMoney(this.minimumCents, cart);
    const messageText = thresholdMet ? this.giftAddedText : this.bannerMessage;

    banner.innerHTML = `
      <div class="cart-gift-progress__header">
        <div class="cart-gift-progress__title">
          <span class="cart-gift-progress__icon" aria-hidden="true">${GIFT_ICON_SVG}</span>
          <span class="cart-gift-progress__label">FREE GIFT</span>
        </div>
        <div class="cart-gift-progress__amount" data-progress-amount>
          <span data-progress-current>${escapeHtml(currentFormatted)}</span>
          <span class="cart-gift-progress__amount-divider">/</span>
          <span data-progress-target>${escapeHtml(targetFormatted)}</span>
        </div>
      </div>
      <div class="cart-gift-progress__progress" data-progress>
        <div class="cart-gift-progress__progress-bar" data-progress-bar style="width: ${percent}%;"></div>
      </div>
      <div class="cart-gift-progress__message" data-message>${escapeHtml(
        messageText,
      )}</div>
    `;
  }
}

const GIFT_ICON_SVG = `
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
    <polyline points="20 12 20 22 4 22 4 12"></polyline>
    <rect x="2" y="7" width="20" height="5"></rect>
    <line x1="12" y1="22" x2="12" y2="7"></line>
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
  </svg>
`;

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatMoney(cents, cart) {
  // Prefer the theme's formatter when available so we honour the merchant's
  // configured money format. Fall back to the cart's currency code or a
  // plain dollar string when the theme helper isn't loaded.
  if (
    typeof window !== "undefined" &&
    window.theme?.Currency?.formatMoney &&
    window.theme?.settings?.moneyFormat
  ) {
    return window.theme.Currency.formatMoney(
      cents,
      window.theme.settings.moneyFormat,
    );
  }
  if (
    typeof window !== "undefined" &&
    typeof window.Shopify?.formatMoney === "function"
  ) {
    return window.Shopify.formatMoney(cents);
  }
  const amount = (Number(cents) / 100).toFixed(2);
  const currency = cart?.currency || "USD";
  return `${amount} ${currency}`;
}

customElements.define("cart-gift-progress", CartGiftProgress);
